export interface paths {
    "/reports": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Creates a new report
         * @description Creates a report from the supplied basic data and an initial set of empty criterion values for all criteria of the supplied catalog. Returns the database id of the created report. The set of criterion values is fixed. Afterwards no new criterion values can be added and none can be deleted.
         */
        post: operations["createReport"];
        /**
         * Deletes the supplied reports
         * @description Deletes one or more reports. In case of an error no reports will be deleted.
         */
        delete: operations["deleteReports"];
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/artifact-name-and-date-of-report": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Checks if the combination of supplied artifact name and date of report already exists
         * @description Checks if the combination of supplied artifact name (website or application) and date of report already exists in the database
         */
        get: operations["checkArtifactNameAndDateOfReport"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/recheck-id/validate": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Checks if the supplied recheck id can be assigned to the supplied report
         * @description Checks if the report with the supplied recheck id can be assigned as predecessor of the report with the supplied bfit id. The report date and the inspection authority are read from the stored report, they are not supplied by the client. Nothing is persisted. The same validation is repeated when the report is saved.
         */
        post: operations["validateRecheckId"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/{reportId}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        post?: never;
        /**
         * Deletes a report
         * @description Deletes a report from the database
         */
        delete: operations["deleteReport"];
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/{reportId}/basics1": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Loads the report basic data (step 1)
         * @description Loads the report basic data (step 1). This includes the catalog name, the artifact name, the url, the report date, the bfit id, the recheck id, the application type, the survey type, and the inspection authority.
         */
        get: operations["getReportBasics1"];
        /**
         * Updates the modifiable report basic data (step 1)
         * @description Updates the modifiable basis data of an existing report (step 1)
         */
        put: operations["updateReportBasics1"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/{reportId}/basics2": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Loads the report basic data (step 2)
         * @description Loads the report basic data (step 2)
         */
        get: operations["getReportBasics2"];
        /**
         * Updates the modifiable report basic data (step 2)
         * @description Updates the modifiable basis data of an existing report (step 2)
         */
        put: operations["updateReportBasics2"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/{reportId}/criteria": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /**
         * Deprecated, please use PUT /v2/reports/{reportId}/criteria instead.
         * @deprecated
         */
        put: operations["updateCriterionValues"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/{reportId}/nonwebdocs": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /** Creates a new non-web-document according to EN301549 Abs. 10 for the supplied report (step 4) */
        post: operations["createNonWebDoc"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/{reportId}/submit": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /**
         * Changes the report status to DISPATCHED and submits its data to BFIT
         * @deprecated
         */
        put: operations["submitReport"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/file": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Imports a report from a file.
         * @description Imports a report from a file. Supported formats are EXCEL and JSON.\
         *     Please note that file in the multipart request must have the appropriate Content-Type set.
         *     * EXCEL: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
         *     * JSON: `application/json`
         */
        post: operations["importReportFile"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/file/validate": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Validates a report import file without persisting it.
         * @description Validates a report from a file and returns field-by-field validation results.
         *     Supported formats are EXCEL and JSON.
         *     The report is not created in the database.
         *     Please note that file in the multipart request must have the appropriate Content-Type set.
         *     * EXCEL: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
         *     * JSON: `application/json`
         */
        post: operations["validateReportFile"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/reports/json": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Imports a report from an JSON structure
         * @description Creates a report from the supplied complete data. All supplied criterion values must pass to criteria in the underlying catalog. Returns the database id of the created report.
         */
        post: operations["importReportFromJson"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/{reportId}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Loads the complete report
         * @description Loads the complete report basic data
         */
        get: operations["readReport"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/{reportId}/criteria": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /**
         * Updates the test results (criterion values) of the report
         * @description Only the supplied subset of test results will be modified. Other existing criterion values of the report will remain unchanged. No criterion values will be deleted.
         */
        put: operations["updateCriteria"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/{reportId}/documents": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /** Updates the test results (criterion values) of all non-web-documents according to EN301549 Abs. 10. Test results of non-web-documents which are not contained in the parameter list will be deleted. */
        put: operations["updateDocuments"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/{reportId}/additionalcriteria": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /**
         * Updates the test results (additional criterion values) of the report
         * @description Only the supplied subset of test results will be modified. Other existing criterion values of the report will remain unchanged. No additional criterion values will be deleted.
         */
        put: operations["updateAdditionalCriteria"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/export": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Export selected reports as a zip file with Excel files.
         * @description Exports reports with provided id's to a file. Resulting file is a zip file containing Excel files with reports.
         */
        post: operations["exportMultiReports"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/{reportId}/export": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Exports the report as an Excel file
         * @description Exports report with provided id to an Excel file.
         */
        post: operations["exportSingleReport"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/delete": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Allows deletion of large amounts of reports via POST body
         * @description Allows deletion of large amounts of reports by using POST body instead of query parameters in cas ethe list is too long and breaks technical limitations
         */
        post: operations["bulkDeleteReports"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/reports/status-change": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /**
         * Changes status of all reports in a list
         * @description Changes status of all reports in a list
         */
        put: operations["changeStatusMulti"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/catalogs": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Returns the basic attributes of all catalogs which are valid at the supplied date.
         * @description Returns the database id, the name, the application type and the survey type of all catalogs which are valid at the supplied date.
         */
        get: operations["getCatalogs"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/catalogs/excel": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Imports a catalog definition from an Excel file
         * @description Imports a catalog definition from an Excel file
         */
        post: operations["importCatalogFromExcel"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/catalogs/{catalogId}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Returns complete data of a supplied catalog with minimum recomendations for supplied survey type
         * @description Returns complete data of a supplied catalog with minimum recomendations for supplied survey type
         */
        get: operations["getCatalog"];
        put?: never;
        post?: never;
        /**
         * Deletes a catalog
         * @description Deletes a catalog. This can ony be done, if there are no reports present that are base on that catalog.
         */
        delete: operations["deleteCatalog"];
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/catalogs/{catalogId}/template": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Generates and returns the Excel import template or an JSON example for a catalog definition
         * @description Generates and returns the Excel import template for the catalog definition with the supplied database id
         */
        get: operations["getImportTemplate"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/user/info": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Read the users information, e.g. name and permissions
         * @description Endpoint to obtain general information about the user including permissions
         */
        get: operations["getUserInfo"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/user/api-key": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Read the users api key
         * @description Endpoint to obtain the api key
         */
        get: operations["getApiKey"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/user/api-key/exists": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /** Checks if an API key exists */
        get: operations["checkApiKeyExists"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/analysis": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /** Generates key figures */
        post: operations["generateKeyFigures"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/analysis/available": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /** Returns available key figures */
        get: operations["getAvailableKeyFigures"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/search/report": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /** Searches for reports and returns the basis data */
        post: operations["searchReports"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/search/report/excel": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Searches a list of reports and returns them in an excel file.
         * @description Generates an excel file where mobile application and websites (application type) are listed on their own sheet each.
         *     Documents are also listed in their own sheet.
         */
        post: operations["searchReportsAsExcel"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/v2/search/catalog": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /** Searches for reports and returns the basis data */
        post: operations["searchCatalogs"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/admin/logs": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /** Download the application log files. */
        get: operations["getApplicationLogs"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/admin/api-keys": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /** Get all users having an API key */
        get: operations["getApiKeyUsers"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/admin/api-key/{userId}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /** Get the API key for a specific user */
        get: operations["getApiKeyForUser"];
        put?: never;
        /** Creates an API key for a specific Azure user */
        post: operations["createApiKey"];
        /** Delete an API key for a specific Azure user */
        delete: operations["deleteApiKey"];
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/admin/report-visibility": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        /** Updates visibility for a list of reports */
        put: operations["updateReportVisibilities"];
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/public/report/{reportId}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Gets a specific publicly accessible report
         * @description Returns the complete data of a report that is marked as publicly accessible.
         *     This endpoint does not require authentication.
         */
        get: operations["getPublicReport"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/public/search-reports": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Searches for publicly accessible reports
         * @description Searches for reports that are marked as publicly accessible and have status DISPATCHED or COMPLETED.
         *     This endpoint does not require authentication.
         */
        post: operations["searchPublicReports"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/public/export-reports/{type}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        get?: never;
        put?: never;
        /**
         * Exports publicly accessible reports in the specified format
         * @description Exports publicly accessible reports based on search criteria.
         *     Supports JSON and Excel formats, both based on the PublicReportsWorkbook structure.
         *     This endpoint does not require authentication.
         */
        post: operations["exportPublicReports"];
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
    "/public/pdf-report/{reportId}": {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        /**
         * Gets the original pdf as report of the specific publicly accessible report
         * @description Returns the pdf version of a report as blob
         *     This endpoint does not require authentication.
         */
        get: operations["getPublicPdfReport"];
        put?: never;
        post?: never;
        delete?: never;
        options?: never;
        head?: never;
        patch?: never;
        trace?: never;
    };
}
export type webhooks = Record<string, never>;
export interface components {
    schemas: {
        /**
         * @description Possible result values for yes/no/not_checked fields
         * @enum {string}
         */
        BooleanResult: BooleanResult;
        ValidationErrorDTO: {
            message: string;
            fieldName: string;
            errorCode: components["schemas"]["ErrorCode"];
        };
        StringMessageErrorResponseDTO: {
            /** @enum {string} */
            payloadType: StringMessageErrorResponseDTO;
            payload: string;
        };
        ValidationFailedErrorResponseDTO: {
            /** @enum {string} */
            payloadType: ValidationFailedErrorResponseDTO;
            payload: components["schemas"]["ValidationErrorDTO"][];
        };
        /**
         * @description The current language of the client
         * @default de
         * @enum {string}
         */
        Language: Language;
        /**
         * @description Application type
         * @enum {string}
         */
        ApplicationType: ApplicationType;
        /**
         * @description Survey type
         * @enum {string}
         */
        SurveyType: SurveyType;
        /**
         * @description The federal state a user is assigned to (corresponds to the Azure group)
         * @enum {string}
         */
        InspectionAuthority: InspectionAuthority;
        /** @description BFIT-Id */
        BfitIdTypeDTO: string;
        ReportBasics1DTO: {
            /**
             * Format: date
             * @description Report date
             */
            dateOfReport: string;
            applicationType: components["schemas"]["ApplicationType"];
            surveyType: components["schemas"]["SurveyType"];
            /**
             * @description Name of the corresponding catalog
             * @example catalogWebsite2023
             */
            catalogName: string;
            /** @description Inspection authority */
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            /** @description Name of the website or application */
            artifactName: string;
            /** @description URL of the website */
            url?: string;
            bfitId?: components["schemas"]["BfitIdTypeDTO"];
            /** @description BFIT id of the report to repeat, if it is a repeating test of an older test */
            recheckId?: components["schemas"]["BfitIdTypeDTO"];
        };
        /**
         * @description Id (UUID) for use as request parameter
         * @example 550e8400-e29b-11d4-a716-446655440000
         */
        IdTypeDTO: string;
        /** @description Wrapper for returning an id and bfitId of a report within a json response. */
        ReportIdWrapperDTO: {
            id: components["schemas"]["IdTypeDTO"];
            bfitId: components["schemas"]["BfitIdTypeDTO"];
        };
        /**
         * @description Error code
         * @enum {string}
         */
        ErrorCode: ErrorCode;
        ErrorDTO: components["schemas"]["ValidationFailedErrorResponseDTO"] | components["schemas"]["StringMessageErrorResponseDTO"];
        /** @description A boolean return value */
        BooleanWrapperDTO: {
            value: boolean;
        };
        /** @description BFIT-Id of a report that is used for the recheck workflow */
        RecheckIdTypeDTO: string;
        /** @description Recheck id to validate for an existing report */
        RecheckIdValidationDTO: {
            /** @description BFIT id of the report the recheck id has been entered for */
            bfitId: components["schemas"]["BfitIdTypeDTO"];
            /** @description BFIT id of the report to repeat. A blank value is valid, because the field is optional. */
            recheckId?: components["schemas"]["RecheckIdTypeDTO"];
        };
        ReportBasics1ModifiableDTO: {
            /** @description Inspection authority */
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            /** @description Name of the website or application */
            artifactName: string;
            /** @description URL der Website */
            url?: string;
            /** @description BFIT id of the report to repeat, if it is a repeating test of an older test */
            recheckId?: components["schemas"]["BfitIdTypeDTO"];
        };
        /**
         * @description OS / Browser / Assistive technologies
         * @enum {string}
         */
        SystemDetailType: SystemDetailType;
        SystemDetailDTO: {
            systemDetailType: components["schemas"]["SystemDetailType"];
            /** @example Version 113.0.5672.126 */
            version?: string;
        };
        /**
         * @description Administration layer
         * @enum {string}
         */
        AdministrationLayer: AdministrationLayer;
        /**
         * @description Public services
         * @enum {string}
         */
        PublicServiceType: PublicServiceType;
        ReportBasics2DTO: {
            systemDetails: components["schemas"]["SystemDetailDTO"][];
            /** @description Editor or evaluator */
            editor?: string;
            /**
             * Format: double
             * @description Duration of the report (hours)
             * @example 3,5
             */
            durationHours?: number;
            administrationLayer: components["schemas"]["AdministrationLayer"];
            /** @description List of the public services of the report */
            publicServices: components["schemas"]["PublicServiceType"][];
            bfitId?: components["schemas"]["BfitIdTypeDTO"];
        };
        /**
         * @description Type of a top level section of a catalog definition
         * @enum {string}
         */
        SectionType: SectionType;
        /**
         * @description Icon which may be shown on the section text
         * @enum {string}
         */
        ValidationResult: ValidationResult;
        /**
         * @deprecated
         * @description Section or criterion definition including the test result (criterion value)
         */
        CriterionWithValueDTO: {
            criterionId?: components["schemas"]["IdTypeDTO"];
            reportId?: components["schemas"]["IdTypeDTO"];
            nonWebDocId?: components["schemas"]["IdTypeDTO"];
            sectionType?: components["schemas"]["SectionType"];
            /** @description True, if the criterion definition may have a test result (criterion value) */
            leaf?: boolean;
            /** @example 9.1.1.1 */
            wcagNumber?: string;
            /** @example Nicht-Text-Inhalt (C) */
            title?: string;
            /** @example Alle Nicht-Text-Inhalte, die dem Benutzer präsentiert werden, haben eine Textalternative, die einem äquivalenten Zweck dient, mit Ausnahme der unten aufgelisteten Situationen. */
            description?: string;
            /** @example AAA */
            level?: string;
            validationResult?: components["schemas"]["ValidationResult"];
            /** @description Sub sections or criteria definition. Only supplied, if the criterion is not a leaf. */
            childCriteria?: components["schemas"]["CriterionWithValueDTO"][];
            checkResult: components["schemas"]["CheckResult"];
            /** @description The test criterion belongs to the category "perceivable" */
            perceivable?: boolean;
            /** @description The test criterion belongs to the category "operable" */
            operable?: boolean;
            /** @description The test criterion belongs to the category "understandable" */
            understandable?: boolean;
            /** @description The test criterion belongs to the category "robust" */
            robust?: boolean;
        };
        /**
         * @description Possible test result values
         * @enum {string}
         */
        CheckResult: CheckResult;
        /**
         * @description file format
         * @enum {string}
         */
        DocumentType: DocumentType;
        /** @description Basic definition for a non-web-document according to EN301549 Abs. 10 */
        NonWebDocBasicsDTO: {
            id: components["schemas"]["IdTypeDTO"];
            documentName: string;
            documentType?: components["schemas"]["DocumentType"];
            isPAC: boolean;
        };
        /** @description Test results (criterion values) of a non-web-document according to EN301549 Abs. 10 */
        NonWebDocDTO: {
            nonWebDocBasics: components["schemas"]["NonWebDocBasicsDTO"];
            criteriaWithValue: components["schemas"]["CriterionWithValueDTO"][];
        };
        /** @description Validation result for a single field including the original imported value */
        FieldValidationResultDTO: {
            /** @description True if the field passed validation */
            valid: boolean;
            /** @description Original imported value mirrored back to the client (any JSON type) */
            value: unknown;
            /** @description Set only when valid is false */
            errorCode?: components["schemas"]["ErrorCode"];
        };
        /**
         * @description Validation status for list/map containers.
         *     VALID: collection has entries and all items passed.
         *     INVALID_LIST_EMPTY: collection has no entries.
         *     INVALID_ITEM: collection has entries but at least one item failed.
         * @enum {string}
         */
        ListValidationStatus: ListValidationStatus;
        /** @description Validation results for one system detail entry */
        SystemDetailItemValidationDTO: {
            systemDetailType: components["schemas"]["FieldValidationResultDTO"];
            version: components["schemas"]["FieldValidationResultDTO"];
        };
        /** @description Validation container for system details */
        SystemDetailsValidationDTO: {
            status: components["schemas"]["ListValidationStatus"];
            /** @description Per-entry validation results in import order */
            items: components["schemas"]["SystemDetailItemValidationDTO"][];
        };
        /** @description Validation container for a list of entries */
        ListValidationResultDTO: {
            status: components["schemas"]["ListValidationStatus"];
            /** @description Per-entry validation results in import order */
            items: components["schemas"]["FieldValidationResultDTO"][];
        };
        /**
         * @description Validation result for one criterion or additional criterion entry.
         *     Extends FieldValidationResultDTO with a key (WCAG number or additional criterion key)
         *     and an optional display label from the catalog.
         */
        CriteriaItemResultDTO: components["schemas"]["FieldValidationResultDTO"] & {
            /** @description WCAG number or additional criterion business key */
            key: string | null;
            /**
             * @description Display title from the catalog (German title).
             *     Null when the key is unknown or not present in the catalog.
             */
            label?: string | null;
        };
        /** @description Validation container for criteria or additional criteria as a list in import order */
        CriteriaValidationResultDTO: {
            status: components["schemas"]["ListValidationStatus"];
            /** @description Per-entry validation results in import order */
            items: components["schemas"]["CriteriaItemResultDTO"][];
        };
        /** @description Validation results for one imported non-web document */
        ReportImportDocumentValidationDTO: {
            documentName: components["schemas"]["FieldValidationResultDTO"];
            documentType: components["schemas"]["FieldValidationResultDTO"];
            pacRequired: components["schemas"]["FieldValidationResultDTO"];
            pacCheckResult: components["schemas"]["FieldValidationResultDTO"];
            criteria: components["schemas"]["CriteriaValidationResultDTO"];
        };
        /** @description Validation container for imported documents */
        DocumentsValidationDTO: {
            status: components["schemas"]["ListValidationStatus"];
            /** @description Per-document validation results in import order */
            items: components["schemas"]["ReportImportDocumentValidationDTO"][];
        };
        /**
         * @description Complete field-by-field validation result for a report import file.
         *     Flat structure for direct JavaScript property access.
         *     Scalars are FieldValidationResultDTO; lists/maps use containers with status + items.
         */
        ReportImportValidationResultDTO: {
            /** @description True only if every nested validation succeeded */
            valid: boolean;
            dateOfReport: components["schemas"]["FieldValidationResultDTO"];
            applicationType: components["schemas"]["FieldValidationResultDTO"];
            surveyType: components["schemas"]["FieldValidationResultDTO"];
            inspectionAuthority: components["schemas"]["FieldValidationResultDTO"];
            artifactName: components["schemas"]["FieldValidationResultDTO"];
            url: components["schemas"]["FieldValidationResultDTO"];
            recheckId: components["schemas"]["FieldValidationResultDTO"];
            editor: components["schemas"]["FieldValidationResultDTO"];
            durationHours: components["schemas"]["FieldValidationResultDTO"];
            administrationLayer: components["schemas"]["FieldValidationResultDTO"];
            publicAccessible: components["schemas"]["FieldValidationResultDTO"];
            systemDetails: components["schemas"]["SystemDetailsValidationDTO"];
            publicServices: components["schemas"]["ListValidationResultDTO"];
            criteria: components["schemas"]["CriteriaValidationResultDTO"];
            additionalCriteria: components["schemas"]["CriteriaValidationResultDTO"];
            documents: components["schemas"]["DocumentsValidationDTO"];
        };
        /**
         * @description Tuple with wcagNumber and value to use in get- and update-requests
         * @example {
         *       "criteriaExample": {
         *         "$ref": "../examples.yaml#/components/examples/criterionResultExample"
         *       }
         *     }
         */
        CriterionResultDTO: {
            wcagNumber: string;
            checkResult: components["schemas"]["CheckResult"];
        };
        AdditionalCriterionResultDTO: {
            /** @description Unique (for a catalog) business id of an additional criterion */
            key: string;
            /** @description Possible values of checkResult are from enums BooleanResult or CheckResult. Which one should be taken depends on the value of catalog.additionalCriteria.resultType */
            checkResult: string;
        };
        /** @description PAC check (PDF accessibility checker), automatic document check run by a software, sufficient for simple website checks */
        PacDTO: {
            required: boolean;
            checkResult: components["schemas"]["CheckResult"];
        };
        /**
         * @description Test results (criterion values) of a non-web-document according to EN301549 Abs. 10
         * @example {
         *       "documentExample": {
         *         "$ref": "../examples.yaml#/components/examples/documentExample"
         *       }
         *     }
         */
        DocumentDTO: {
            id?: components["schemas"]["IdTypeDTO"];
            documentName: string;
            documentType?: components["schemas"]["DocumentType"];
            pac: components["schemas"]["PacDTO"];
            criteria: components["schemas"]["CriterionResultDTO"][];
        };
        /**
         * @description represents a complete report inclusive criteria lists
         * @example {
         *       "reportImportExample": {
         *         "$ref": "../examples.yaml#/components/examples/reportImportExample"
         *       }
         *     }
         */
        ReportImportDTO: {
            /**
             * Format: date
             * @description Report date
             * @example 2024-01-01
             */
            dateOfReport: string;
            applicationType: components["schemas"]["ApplicationType"];
            surveyType: components["schemas"]["SurveyType"];
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            /**
             * @description Name of the website or application
             * @example Example Webpage
             */
            artifactName: string;
            /**
             * @description URL of the website
             * @example www.google.com
             */
            url?: string;
            recheckId?: components["schemas"]["RecheckIdTypeDTO"];
            systemDetails?: components["schemas"]["SystemDetailDTO"][];
            /**
             * @description Editor or evaluator
             * @example Maximilian Müller
             */
            editor?: string;
            /**
             * Format: double
             * @description Duration of the report (hours)
             * @example 3,5
             */
            durationHours?: number;
            administrationLayer?: components["schemas"]["AdministrationLayer"];
            /** @description List of the public services of the report */
            publicServices?: components["schemas"]["PublicServiceType"][];
            /**
             * @description Indicates whether the report is publicly accessible
             * @default false
             */
            publicAccessible: boolean;
            criteria: components["schemas"]["CriterionResultDTO"][];
            additionalCriteria: components["schemas"]["AdditionalCriterionResultDTO"][];
            documents?: components["schemas"]["DocumentDTO"][];
        };
        /**
         * @description Report status
         * @enum {string}
         */
        ReportStatusType: ReportStatusType;
        /**
         * @description represents metadata about a report
         * @example {
         *       "reportExample": {
         *         "$ref": "../examples.yaml#/components/examples/reportExample"
         *       }
         *     }
         */
        ReportMetadataDTO: {
            id: components["schemas"]["IdTypeDTO"];
            /**
             * Format: date
             * @description Report date
             * @example 2024-01-01
             */
            dateOfReport: string;
            surveyType: components["schemas"]["SurveyType"];
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            /**
             * @description Name of the website or application
             * @example Example Webpage
             */
            artifactName: string;
            /**
             * @description URL of the website
             * @example www.google.com
             */
            url?: string;
            recheckId?: components["schemas"]["RecheckIdTypeDTO"];
            bfitId?: components["schemas"]["BfitIdTypeDTO"];
            systemDetails?: components["schemas"]["SystemDetailDTO"][];
            /**
             * @description Editor or evaluator
             * @example Maximilian Müller
             */
            editor?: string;
            status: components["schemas"]["ReportStatusType"];
            /**
             * Format: double
             * @description Duration of the report (hours)
             * @example 3,5
             */
            durationHours?: number;
            administrationLayer?: components["schemas"]["AdministrationLayer"];
            /** @description List of the public services of the report */
            publicServices?: components["schemas"]["PublicServiceType"][];
            /**
             * @description Indicates whether the report is publicly accessible
             * @default false
             */
            publicAccessible: boolean;
        };
        /**
         * @description Basic definition of a criterion
         * @example {
         *       "criteriaExample": {
         *         "$ref": "../examples.yaml#/components/examples/criteriaExample"
         *       }
         *     }
         */
        CriterionDTO: {
            /** @example Nicht-Text-Inhalt (C) */
            title: string;
            /** @example 9.1.1.1 */
            wcagNumber: string;
            /** @example Alle Nicht-Text-Inhalte, die dem Benutzer präsentiert werden, haben eine Textalternative, die einem äquivalenten Zweck dient, mit Ausnahme der unten aufgelisteten Situationen. */
            description?: string;
            /** @example Level AAA */
            level?: string;
            /** @description This criterion is minimal recommendation for the respective surveyType */
            minRecommendation?: boolean;
            leaf: boolean;
            /** @description Sub sections or criteria definition. */
            childCriteria: components["schemas"]["CriterionDTO"][];
        };
        /**
         * @description Possible types of additional criterion attribute
         * @enum {string}
         */
        AdditionalCriterionResultType: AdditionalCriterionResultType;
        AdditionalCriterionDTO: {
            /** @example Erläuterungen in Gebärdensprache vorhanden */
            title: string;
            /** @description Unique (for a catalog) business id of an additional criterion */
            key: string;
            /** @example Lorem ipsum dolor sit */
            description?: string;
            resultType: components["schemas"]["AdditionalCriterionResultType"];
            /** @description This criterion is minimal recommendation for the respective surveyType */
            minRecommendation: boolean;
        };
        /**
         * @description Basic definition of a catalog
         * @example {
         *       "catalogExample": {
         *         "$ref": "../examples.yaml#/components/examples/catalogExample"
         *       }
         *     }
         */
        CatalogDTO: {
            catalogId: components["schemas"]["IdTypeDTO"];
            /** @example PrüfkatalogWebseite2023 */
            catalogName: string;
            applicationType: components["schemas"]["ApplicationType"];
            /**
             * Format: date
             * @example 2023-01-01
             */
            validFrom: string;
            /**
             * Format: date
             * @example 2023-12-31
             */
            validUntil: string;
            reportCriteria: components["schemas"]["CriterionDTO"][];
            documentCriteria: components["schemas"]["CriterionDTO"][];
            additionalCriteria: components["schemas"]["AdditionalCriterionDTO"][];
        };
        /**
         * @example {
         *       "reportExample": {
         *         "$ref": "../examples.yaml#/components/examples/reportExample"
         *       }
         *     }
         */
        ReportDTO: components["schemas"]["ReportMetadataDTO"] & {
            catalog: components["schemas"]["CatalogDTO"];
            criteria: components["schemas"]["CriterionResultDTO"][];
            additionalCriteria: components["schemas"]["AdditionalCriterionResultDTO"][];
            documents?: components["schemas"]["DocumentDTO"][];
        };
        /** @description Parameter structure for status-change request. Contains new status and a list of report ids, which status should be changed */
        ReportStatusChangeDTO: {
            newStatus: components["schemas"]["ReportStatusType"];
            reportIdList: components["schemas"]["IdTypeDTO"][];
        };
        /** @description Basic definition of a catalog */
        CatalogBasicsDTO: {
            catalogId: components["schemas"]["IdTypeDTO"];
            catalogName: string;
            applicationType: components["schemas"]["ApplicationType"];
            /** Format: date */
            validFrom: string;
            /** Format: date */
            validUntil: string;
        };
        /** @description Wrapper for returning an id within a json response. */
        IdWrapperDTO: {
            id: components["schemas"]["IdTypeDTO"];
        };
        /**
         * @description format of the report template
         * @enum {string}
         */
        FormatType: FormatType;
        /**
         * @description The level of permissions a user has (corresponds to the Azure role)
         * @enum {string}
         */
        PermissionLevel: PermissionLevel;
        /** @description Contains a users basic information (e.g. name) and permissions */
        UserDTO: {
            id: string;
            givenName: string;
            surname: string;
            permissionLevel: components["schemas"]["PermissionLevel"];
            inspectionAuthorities: components["schemas"]["InspectionAuthority"][];
        };
        /**
         * @description Names of key figures, that can be generated
         *     * `REPORT_OVERVIEW` - Übersicht der vereinfachten und eingehenden Prüfungen (Berichtsübersicht) [1.1.1]
         *     * `REPORTS_PER_ADMINISTRATION_LAYER` - Websites pro Verwaltungsebene (Reports per Verwaltungsebene) [1.1.2]
         *     * `REPORTS_PER_PUBLIC_SERVICE` - Websites pro Kategorie (Reports per Dienstleistung) [1.1.3]
         *     * `REPORTS_PER_OS` - Mobile Anwendungen pro Betriebssystem (Reports nach mobile OS) [1.1.4]
         *     * `BREAKDOWN_OF_SYSTEM_DETAILS` - Aufschlüsselung Details der Berichterstattung (Reports nach Detail) [1.1.6]
         *     * `WEBSITE_SIMPLIFIED_REPORTS_WITHOUT_ADDITIONAL_CRITERIA` - Vereinfachte Prüfungen ohne Zusatzkriterien (Webseite vereinfach ohne Zusatz) [1.1.7]
         *     * `WEBSITE_SIMPLIFIED_REPORTS_ADDITIONAL_CRITERIA_ONLY` - Vereinfachte Prüfungen nur Zusatzkriterien (Webseite vereinfacht Zusatz) [1.1.8]
         *     * `WEBSITE_DETAILED_REPORTS_WITHOUT_ADDITIONAL_CRITERIA` - Eingehende Prüfungen ohne Zusatzkriterien (Webseite eingehend ohne Zusatz) [1.1.9]
         *     * `WEBSITE_DETAILED_REPORTS_ADDITIONAL_CRITERIA_ONLY` - Eingehende Prüfungen nur Zusatzkriterien (Webseite eingehend Zusatz) [1.1.10]
         *     * `MOBILE_APP_REPORTS_WITHOUT_ADDITIONAL_CRITERIA` - Mobile Anwendungen ohne Zusatzkriterien (mobile Anwendungen ohne Zusatz) [1.1.11]
         *     * `MOBILE_APP_REPORTS_ADDITIONAL_CRITERIA_ONLY` - Mobile Anwendungen nur Zusatzkriterien (mobile Anwendungen Zusatz) [1.1.12]
         *     * `DETAILED_REPORT_DOCUMENTS_CRITERIA` - Dokumente der eingehenden Prüfung (Dokumentenprüfung) [1.1.13]
         *     * `CRITERIA_BREAKDOWN` - Prozentuale Aufteilung (Bewertung nach Anwendung) [1.1.14]
         *     * `WEBSITE_PRINCIPLES_RESULT_COMPARISON` -  Vergleichen/Entwicklung der Ergebnisse bezüglich der vier Prinzipien - nur Webseiten (Webseiten Prinzipien) [1.2.1.1]
         *     * `DOCUMENT_PRINCIPLES_RESULT_COMPARISON` -  Vergleichen/Entwicklung der Ergebnisse bezüglich der vier Prinzipien - nur DOCUMENT (Dokumente Prinzipien) [1.2.1.2]
         *     * `MOBILE_APP_PRINCIPLES_RESULT_COMPARISON` -  Vergleichen/Entwicklung der Ergebnisse bezüglich der vier Prinzipien - nur mobile Anwendungen (mobile Anwendungen Prinzipien) [1.2.1.3]
         *     * `ALL_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - vereinfachte Überwachungsmethode bei Websites - Alle (Webseiten (vereinfacht)) [1.2.2]
         *     * `INITIAL_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - vereinfachte Überwachungsmethode bei Websites -  Erstprüfungen (Webseiten (verein.) Erstprüfung) [1.2.2]
         *     * `RECHECK_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - vereinfachte Überwachungsmethode bei Websites -  Wiederholungsprüfungen (Webseiten (verei.) Wiederholung) [1.2.2]
         *     * `ALL_WEBSITE_DETAILED_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren -  eingehende Überwachungsmethode bei Websites - Alle (Webseiten (eingehend)) [1.2.2]
         *     * `INITIAL_WEBSITE_DETAILED_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - eingehende Überwachungsmethode bei Websites -  Erstprüfungen (Webseiten (eingeh.) Erstprüfung) [1.2.2]
         *     * `RECHECK_WEBSITE_DETAILED_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - eingehende Überwachungsmethode bei Websites -  Wiederholungsprüfungen (Webseiten (einge.) Wiederholung) [1.2.2]
         *     * `ALL_MOBILE_APP_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - eingehende Überwachungsmethode bei mobilen Anwendungen - Alle (Mobile Anwendungen) [1.2.2]
         *     * `INITIAL_MOBILE_APP_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - eingehende Überwachungsmethode bei mobilen Anwendungen -  Erstprüfungen (Mobile Anwendungen Erstprüfung) [1.2.2]
         *     * `RECHECK_MOBILE_APP_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der erzielten Ergebnisse anhand der durchgeführten Überwachungsverfahren - eingehende Überwachungsmethode bei mobilen Anwendungen -  Wiederholungsprüfungen (Mobile Anwendungen Wiederholung) [1.2.2]
         *     * `ADMINISTRATION_LAYERS_COMPARISON` - Vergleichen/Entwicklung der Ergebnisse bezüglich der verschiedenen Verwaltungsebenen  (Verwaltungsebene) [1.2.3]
         *     * `PUBLIC_SERVICES_COMPARISON` - Vergleichen/Entwicklung der Ergebnisse bezüglich der verschiedenen Dienstleistungsbereiche (Dienstleistungen) [1.2.4]
         *     * `ACCESSIBILITY_DECLARATION_RESULT_COMPARISON` - Vergleichen/Entwicklung der Ergebnisse zur Erklärung zur Barrierefreiheit (Ergebnisse zur EzB) [1.2.5]
         *     * `PAC_PDF_CHECK_RESULTS_COMPARISON` - Vergleichen/Entwicklung der Ergebnisse zu PDF-Dokumenten [nur PAC-Test] (PAC-Prüfung) [1.2.6.1]
         *     * `MANUAL_PDF_CHECK_RESULTS_COMPARISON` - Vergleichen/Entwicklung der Ergebnisse zu PDF-Dokumenten bei manueller Dokumentenprüfung (Manuelle Dokumentenprüfung) [1.2.6.2]
         *     * `ADDITIONAL_CRITERIA_COMPARISON` - Vergleichen/Entwicklung der Ergebnisse zur Deutschen Gebärdensprache, Leichten Sprache und Benutzerfreundlichkeit (Zusatzkriterien) [1.2.7]
         *     * `RECHECK_REPORTS_COMPARISON` - Vergleichen/Entwicklung des Anteils von Wiederholungsprüfungen am Prüfvolumen (1.2.8)
         *     * `SAMPLE_ADMINISTRATION_LAYERS_YEAR_COMPARISON` - Stichprobe: Jahresvergleich Verwaltungsebenen
         *     * `SAMPLE_PUBLIC_SERVICES_YEAR_COMPARISON` - Stichprobe: Jahresvergleich Dienstleistungskategorien (Webauftritte)
         *     * `SAMPLE_MOBILE_OS_YEAR_COMPARISON` - Stichprobe: Jahresvergleich mobile Betriebssysteme
         *     * `SAMPLE_RECHECK_YEAR_COMPARISON` - Stichprobe: Jahresvergleich Wiederholungsprüfung Web und mobile Anwendungen
         *     * `SAMPLE_CHECKS_BY_OS` - Stichprobe: Prüfungen nach Betriebssystem gesamt
         *     * `SAMPLE_CHECKS_BY_BROWSER` - Stichprobe: Prüfungen nach Browser gesamt
         *     * `SAMPLE_CHECKS_BY_SCREENREADER` - Stichprobe: Prüfungen nach Screenreader gesamt
         * @enum {string}
         */
        KeyFigureType: KeyFigureType;
        KeyFiguresRequestDTO: {
            keyFigures: components["schemas"]["KeyFigureType"][];
            /**
             * Format: date
             * @example 2023-01-01
             */
            startDate: string;
            /**
             * Format: date
             * @example 2023-12-31
             */
            endDate: string;
            inspectionAuthorities: components["schemas"]["InspectionAuthority"][];
        };
        /**
         * @description Names of groups of key figures
         * @enum {string}
         */
        KeyFigureCategoryType: KeyFigureCategoryType;
        KeyFigureCategoryDTO: {
            category?: components["schemas"]["KeyFigureCategoryType"];
            keyFigures?: components["schemas"]["KeyFigureType"][];
        };
        /** @enum {string} */
        SearchField: SearchField;
        SortDTO: {
            /** @enum {string} */
            order: SortDTO;
            field: components["schemas"]["SearchField"];
        };
        /** @description represents filterable metadata of report */
        ReportFilterDTO: {
            bfitId?: components["schemas"]["BfitIdTypeDTO"];
            inspectionAuthority?: components["schemas"]["InspectionAuthority"];
            applicationType?: components["schemas"]["ApplicationType"];
            surveyType?: components["schemas"]["SurveyType"];
            artifactName?: string;
            /** Format: date */
            dateOfReportFrom?: string;
            /** Format: date */
            dateOfReportUntil?: string;
            status?: components["schemas"]["ReportStatusType"];
            minRecommendation?: components["schemas"]["ValidationResult"];
            editor?: string;
            administrationLayer?: components["schemas"]["AdministrationLayer"];
            /** @description Filter by public accessibility. If set, only reports with this publicAccessible value are returned. */
            publicAccessible?: boolean;
        };
        SearchReportsRequestDTO: {
            /**
             * @description Zero-based page number, must not be negative.
             *     E.g. `page` of 1 and a `size` of 20 would return the 21st to 40th item.
             */
            page: number;
            /**
             * @description The number or elements to return starting at start.
             *     E.g. `page` of 1 and a `size` of 20 would return the 21st to 40th item.
             */
            size: number;
            sort: components["schemas"]["SortDTO"];
            filter?: components["schemas"]["ReportFilterDTO"];
        };
        /** @description The result of a report search, containing a reports metadata */
        SearchReportsResultDTO: {
            id: string;
            owner: boolean;
            bfitId: components["schemas"]["BfitIdTypeDTO"];
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            applicationType: components["schemas"]["ApplicationType"];
            surveyType: components["schemas"]["SurveyType"];
            artifactName: string;
            /** Format: date */
            dateOfReport: string;
            status: components["schemas"]["ReportStatusType"];
            minRecommendationValid: boolean;
            editor: string;
            administrationLayer: components["schemas"]["AdministrationLayer"];
            /**
             * @description Indicates whether the report is publicly accessible
             * @default false
             */
            publicAccessible: boolean;
        };
        SearchReportsResponseDTO: {
            /**
             * Format: int64
             * @description total number of elements that exist
             */
            total: number;
            /** @description zero-based page number that was requested */
            page: number;
            /** @description size of a page that was requested, *not* the actual size  of `reports` array */
            size: number;
            results: components["schemas"]["SearchReportsResultDTO"][];
        };
        /** @enum {string} */
        SearchCatalogField: SearchCatalogField;
        SortCatalogDTO: {
            /** @enum {string} */
            order: SortCatalogDTO;
            field: components["schemas"]["SearchCatalogField"];
        };
        SearchCatalogsRequestDTO: {
            /**
             * @description Zero-based page number, must not be negative.
             *     E.g. `page` of 1 and a `size` of 20 would return the 21st to 40th item.
             */
            page: number;
            /**
             * @description The number or elements to return starting at start.
             *     E.g. `page` of 1 and a `size` of 20 would return the 21st to 40th item.
             */
            size: number;
            sort: components["schemas"]["SortCatalogDTO"];
        };
        /** @description Metadata of a catalog */
        CatalogMetadataDTO: {
            id: string;
            name: string;
            applicationType: components["schemas"]["ApplicationType"];
            /** Format: date */
            validFrom: string;
            /** Format: date */
            validUntil: string;
            /** Format: int64 */
            reportCount: number;
        };
        SearchCatalogsResponseDTO: {
            /**
             * Format: int64
             * @description total number of elements that exist
             */
            total: number;
            /** @description zero-based page number that was requested */
            page: number;
            /** @description size of a page that was requested, *not* the actual size  of `reports` array */
            size: number;
            results: components["schemas"]["CatalogMetadataDTO"][];
        };
        /** @description Holds an API key, the belonging Azure user id and a description. */
        ApiKeyUserDTO: {
            userId: string;
            description?: string;
        };
        /** @description Contains a report ID and its visibility status */
        ReportVisibilityUpdateDTO: {
            id: components["schemas"]["IdTypeDTO"];
            /** @description Visibility status of the report */
            publicAccessible: boolean;
        };
        /**
         * @description represents a complete public report inclusive criteria lists (without editor and duration information)
         * @example {
         *       "id": "123-456-789",
         *       "dateOfReport": "2023-09-20",
         *       "surveyType": "DETAILED",
         *       "inspectionAuthority": "BE",
         *       "artifactName": "Test Website",
         *       "url": "https://test.de",
         *       "recheckId": null,
         *       "bfitId": "1234",
         *       "systemDetails": [
         *         {
         *           "systemDetailType": "CMS",
         *           "name": "WordPress",
         *           "version": "6.0"
         *         }
         *       ],
         *       "status": "COMPLETED",
         *       "administrationLayer": "NATIONAL",
         *       "publicServices": [
         *         "SOCIAL_PROTECTION"
         *       ],
         *       "applicationType": "WEBSITE",
         *       "criteria": [
         *         {
         *           "criterionId": "criterion-1",
         *           "checkResult": "OK",
         *           "comment": "All requirements met"
         *         }
         *       ],
         *       "additionalCriteria": [
         *         {
         *           "additionalCriterionId": "additional-1",
         *           "checkResult": "WARNING",
         *           "comment": "Minor issues found"
         *         }
         *       ],
         *       "documents": [
         *         {
         *           "id": "doc-1",
         *           "name": "Test Document",
         *           "url": "https://example.com/doc.pdf"
         *         }
         *       ]
         *     }
         */
        PublicReportDTO: {
            id: components["schemas"]["IdTypeDTO"];
            /** @description true if original report exists as PDF */
            hasPdf?: boolean;
            /**
             * Format: date
             * @description Report date
             * @example 2024-01-01
             */
            dateOfReport: string;
            surveyType: components["schemas"]["SurveyType"];
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            /**
             * @description Name of the website or application
             * @example Example Webpage
             */
            artifactName: string;
            /**
             * @description URL of the website
             * @example www.google.com
             */
            url: string;
            recheckId: components["schemas"]["RecheckIdTypeDTO"];
            bfitId: components["schemas"]["BfitIdTypeDTO"];
            systemDetails: components["schemas"]["SystemDetailDTO"][];
            administrationLayer: components["schemas"]["AdministrationLayer"];
            /** @description List of the public services of the report */
            publicServices: components["schemas"]["PublicServiceType"][];
            applicationType: components["schemas"]["ApplicationType"];
            catalog?: components["schemas"]["CatalogDTO"];
            criteria: components["schemas"]["CriterionResultDTO"][];
            additionalCriteria: components["schemas"]["AdditionalCriterionResultDTO"][];
            documents: components["schemas"]["DocumentDTO"][];
        };
        /** @description represents filterable metadata of public report */
        PublicReportFilterDTO: {
            inspectionAuthority?: components["schemas"]["InspectionAuthority"];
            applicationType?: components["schemas"]["ApplicationType"];
            surveyType?: components["schemas"]["SurveyType"];
            artifactName?: string;
            /** Format: date */
            dateOfReportFrom?: string;
            /** Format: date */
            dateOfReportUntil?: string;
            administrationLayer?: components["schemas"]["AdministrationLayer"];
        };
        PublicSearchReportsRequestDTO: {
            /**
             * @description Zero-based page number, must not be negative.
             *     E.g. `page` of 1 and a `size` of 20 would return the 21st to 40th item.
             */
            page: number;
            /**
             * @description The number or elements to return starting at start.
             *     E.g. `page` of 1 and a `size` of 20 would return the 21st to 40th item.
             */
            size: number;
            sort: components["schemas"]["SortDTO"];
            filter?: components["schemas"]["PublicReportFilterDTO"];
        };
        /** @description The result of a public report search, containing a reports metadata */
        PublicSearchReportsResultDTO: {
            id: string;
            bfitId: components["schemas"]["BfitIdTypeDTO"];
            inspectionAuthority: components["schemas"]["InspectionAuthority"];
            applicationType: components["schemas"]["ApplicationType"];
            surveyType: components["schemas"]["SurveyType"];
            artifactName: string;
            /** Format: date */
            dateOfReport: string;
            administrationLayer: components["schemas"]["AdministrationLayer"];
        };
        PublicSearchReportsResponseDTO: {
            /**
             * Format: int64
             * @description total number of elements that exist
             */
            total: number;
            /** @description zero-based page number that was requested */
            page: number;
            /** @description size of a page that was requested, *not* the actual size  of `reports` array */
            size: number;
            results: components["schemas"]["PublicSearchReportsResultDTO"][];
        };
    };
    responses: {
        /** @description Header Authorization or X-API-KEY not present or invalid */
        NotAuthorized: {
            headers: {
                [name: string]: unknown;
            };
            content: {
                "application/json": components["schemas"]["ErrorDTO"];
            };
        };
        /** @description Insufficient permission */
        InsufficientPermission: {
            headers: {
                [name: string]: unknown;
            };
            content: {
                "application/json": components["schemas"]["ErrorDTO"];
            };
        };
    };
    parameters: {
        /** @description The current language of the client */
        LanguageHttpHeader: components["schemas"]["Language"];
    };
    requestBodies: never;
    headers: never;
    pathItems: never;
}
export type $defs = Record<string, never>;
export interface operations {
    createReport: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                /**
                 * @example {
                 *       "applicationType": "WEBSITE",
                 *       "surveyType": "DETAILED",
                 *       "dateOfReport": "2023-09-20",
                 *       "inspectionAuthority": "BE",
                 *       "artifactName": "Test Website",
                 *       "catalogName": "Prüfkatalog Website",
                 *       "url": "https://test.de"
                 *     }
                 */
                "application/json": components["schemas"]["ReportBasics1DTO"];
            };
        };
        responses: {
            /** @description Report created */
            201: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "id": "123-456-789",
                     *       "bfitId": "1234"
                     *     }
                     */
                    "application/json": components["schemas"]["ReportIdWrapperDTO"];
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    deleteReports: {
        parameters: {
            query: {
                /**
                 * @example [
                 *       1234,
                 *       5678
                 *     ]
                 */
                reportIds: components["schemas"]["IdTypeDTO"][];
            };
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description Reports deleted */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    checkArtifactNameAndDateOfReport: {
        parameters: {
            query: {
                artifactName: string;
                dateOfReport: string;
            };
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "value": false
                     *     }
                     */
                    "application/json": components["schemas"]["BooleanWrapperDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    validateRecheckId: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody: {
            content: {
                /**
                 * @example {
                 *       "bfitId": "10001",
                 *       "recheckId": "10000"
                 *     }
                 */
                "application/json": components["schemas"]["RecheckIdValidationDTO"];
            };
        };
        responses: {
            /** @description OK. True if the recheck id can be assigned, otherwise false */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "value": true
                     *     }
                     */
                    "application/json": components["schemas"]["BooleanWrapperDTO"];
                };
            };
            /** @description Bad request. Request body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    deleteReport: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description Report deleted */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getReportBasics1: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "applicationType": "WEBSITE",
                     *       "surveyType": "DETAILED",
                     *       "dateOfReport": "2023-09-20",
                     *       "inspectionAuthority": "BE",
                     *       "artifactName": "Test Website",
                     *       "catalogName": "Prüfkatalog Website",
                     *       "url": "https://test.de"
                     *     }
                     */
                    "application/json": components["schemas"]["ReportBasics1DTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateReportBasics1: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: {
            content: {
                /**
                 * @example {
                 *       "inspectionAuthority": "BE",
                 *       "artifactName": "Test Website (Update)",
                 *       "url": "http://your.updated.url/your/updated/path"
                 *     }
                 */
                "application/json": components["schemas"]["ReportBasics1ModifiableDTO"];
            };
        };
        responses: {
            /** @description Report updated */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getReportBasics2: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "systemDetails": [
                     *         {
                     *           "systemDetailType": "OS_WINDOWS_7",
                     *           "version": "Version 113.0.5672.126"
                     *         }
                     *       ],
                     *       "editor": "Max Mustermann",
                     *       "durationHours": 3.5,
                     *       "administrationLayer": "NATIONAL",
                     *       "publicServices": [
                     *         "SOCIAL_PROTECTION"
                     *       ],
                     *       "bfitId": "42"
                     *     }
                     */
                    "application/json": components["schemas"]["ReportBasics2DTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateReportBasics2: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /**
                 * @example {
                 *       "systemDetails": [
                 *         {
                 *           "systemDetailType": "OS_WINDOWS_7",
                 *           "version": "Version 113.0.5672.126"
                 *         }
                 *       ],
                 *       "editor": "Max Mustermann",
                 *       "durationHours": 3.5,
                 *       "administrationLayer": "NATIONAL",
                 *       "publicServices": [
                 *         "SOCIAL_PROTECTION"
                 *       ],
                 *       "bfitId": "42"
                 *     }
                 */
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["ReportBasics2DTO"];
            };
        };
        responses: {
            /** @description Report updated */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateCriterionValues: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody: {
            content: {
                /**
                 * @example {
                 *       "value": [
                 *         {
                 *           "criterionId": "6b7c369e-390e-4515-a02f-cee0fc2ca0df",
                 *           "sectionType": "REPORT",
                 *           "leaf": false,
                 *           "wcagNumber": "5",
                 *           "title": "Allgemeine Anforderungen",
                 *           "validationResult": "WARNING",
                 *           "description": "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At",
                 *           "childCriteria": [
                 *             {
                 *               "criterionId": "460cbc60-0a1e-4565-91f8-dd5e6c83823e",
                 *               "reportId": "4c0d7a19-638e-4a07-ab0c-04c6a7a8f2a3",
                 *               "sectionType": "REPORT",
                 *               "leaf": true,
                 *               "wcagNumber": "5.1",
                 *               "checkResult": "FAILED",
                 *               "title": "Erhaltung von Barrierefreiheitsinformationen während der Umwandlung",
                 *               "validationResult": "WARNING",
                 *               "description": "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At",
                 *               "childCriteria": [],
                 *               "perceivable": false,
                 *               "operable": false,
                 *               "understandable": false,
                 *               "robust": false,
                 *               "minRecommendationSimplified": false,
                 *               "minRecommendationDetailed": false
                 *             },
                 *             {
                 *               "criterionId": "db43736f-e387-46fd-8bb2-7b075627cdff",
                 *               "sectionType": "REPORT",
                 *               "leaf": false,
                 *               "wcagNumber": "5.2",
                 *               "title": "Bedienbare Elemente",
                 *               "validationResult": "WARNING",
                 *               "description": "",
                 *               "childCriteria": [
                 *                 {
                 *                   "criterionId": "8e6cfcc7-f660-4a12-8041-b4b959af7749",
                 *                   "reportId": "4c0d7a19-638e-4a07-ab0c-04c6a7a8f2a3",
                 *                   "sectionType": "REPORT",
                 *                   "leaf": true,
                 *                   "wcagNumber": "5.2.1",
                 *                   "title": "Möglichkeiten der Bedienung",
                 *                   "validationResult": "OK",
                 *                   "description": "",
                 *                   "checkResult": "PASSED",
                 *                   "childCriteria": [],
                 *                   "perceivable": false,
                 *                   "operable": false,
                 *                   "understandable": false,
                 *                   "robust": false,
                 *                   "minRecommendationSimplified": true,
                 *                   "minRecommendationDetailed": true
                 *                 },
                 *                 {
                 *                   "criterionId": "40b54992-c58d-44b4-bcd0-360a040b3f95",
                 *                   "reportId": "4c0d7a19-638e-4a07-ab0c-04c6a7a8f2a3",
                 *                   "sectionType": "REPORT",
                 *                   "leaf": true,
                 *                   "wcagNumber": "5.2.2",
                 *                   "title": "Unterscheidbarkeit der bedienbaren Elemente",
                 *                   "validationResult": "WARNING",
                 *                   "description": "",
                 *                   "childCriteria": [],
                 *                   "checkResult": "FAILED",
                 *                   "perceivable": false,
                 *                   "operable": false,
                 *                   "understandable": false,
                 *                   "robust": false,
                 *                   "minRecommendationSimplified": false,
                 *                   "minRecommendationDetailed": false
                 *                 }
                 *               ],
                 *               "perceivable": false,
                 *               "operable": false,
                 *               "understandable": false,
                 *               "robust": false,
                 *               "minRecommendationSimplified": true,
                 *               "minRecommendationDetailed": false
                 *             }
                 *           ],
                 *           "perceivable": false,
                 *           "operable": false,
                 *           "understandable": false,
                 *           "robust": false,
                 *           "minRecommendationSimplified": false,
                 *           "minRecommendationDetailed": false
                 *         },
                 *         {
                 *           "criterionId": "29fb31fd-5635-4650-a989-db0a35bae43c",
                 *           "sectionType": "REPORT",
                 *           "leaf": false,
                 *           "wcagNumber": "6",
                 *           "title": "IKT mit Zweiwege-Sprachkommunikation",
                 *           "validationResult": "OK",
                 *           "description": "",
                 *           "childCriteria": [
                 *             {
                 *               "criterionId": "2476ccf8-8f4b-4f9a-ad19-d672ca0569fc",
                 *               "reportId": "4c0d7a19-638e-4a07-ab0c-04c6a7a8f2a3",
                 *               "sectionType": "REPORT",
                 *               "leaf": true,
                 *               "wcagNumber": "6.1",
                 *               "title": "Audio-Bandbreite für Sprache",
                 *               "validationResult": "OK",
                 *               "description": "",
                 *               "childCriteria": [],
                 *               "checkResult": "PASSED",
                 *               "perceivable": false,
                 *               "operable": false,
                 *               "understandable": false,
                 *               "robust": false,
                 *               "minRecommendationSimplified": false,
                 *               "minRecommendationDetailed": false
                 *             }
                 *           ],
                 *           "perceivable": false,
                 *           "operable": false,
                 *           "understandable": false,
                 *           "robust": false,
                 *           "minRecommendationSimplified": false,
                 *           "minRecommendationDetailed": false
                 *         }
                 *       ]
                 *     }
                 */
                "application/json": components["schemas"]["CriterionWithValueDTO"][];
            };
        };
        responses: {
            /** @description update successful */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    createNonWebDoc: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /**
                 * @example {
                 *       "id": 42
                 *     }
                 */
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "value": {
                     *         "nonWebDocBasics": {
                     *           "id": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *           "documentName": "doc1.pdf",
                     *           "isPAC": false
                     *         },
                     *         "criteriaWithValue": [
                     *           {
                     *             "criterionId": "95ea980a-71b4-4cb4-8249-ef0a6389bfd8",
                     *             "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *             "sectionType": "NON_WEB_DOC",
                     *             "leaf": false,
                     *             "wcagNumber": "PAC_CHECK_RESULTS",
                     *             "title": "PAC-Prüfergebnisse",
                     *             "description": "",
                     *             "level": "A",
                     *             "validationResult": null,
                     *             "childCriteria": [
                     *               {
                     *                 "criterionId": "3a712a85-951f-41e0-acfa-f83ced5dd015",
                     *                 "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                 "sectionType": "NON_WEB_DOC",
                     *                 "leaf": true,
                     *                 "wcagNumber": "PAC_CHECK_RESULTS.RESULT",
                     *                 "title": "Prüfergebnis",
                     *                 "description": "",
                     *                 "level": "A",
                     *                 "validationResult": null,
                     *                 "childCriteria": [],
                     *                 "checkResult": "PASSED",
                     *                 "perceivable": false,
                     *                 "operable": false,
                     *                 "understandable": false,
                     *                 "robust": false
                     *               }
                     *             ],
                     *             "checkResult": "EMPTY",
                     *             "perceivable": false,
                     *             "operable": false,
                     *             "understandable": false,
                     *             "robust": false
                     *           },
                     *           {
                     *             "criterionId": "bc26265b-80ee-4e75-84ae-0900a60cdb6a",
                     *             "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *             "sectionType": "NON_WEB_DOC",
                     *             "leaf": false,
                     *             "wcagNumber": "10",
                     *             "title": "Nicht-Web-Dokumente",
                     *             "description": "",
                     *             "level": "AAA",
                     *             "validationResult": null,
                     *             "childCriteria": [
                     *               {
                     *                 "criterionId": "cb90d108-a33e-46f0-a25d-d55c01a561a2",
                     *                 "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                 "sectionType": "NON_WEB_DOC",
                     *                 "leaf": false,
                     *                 "wcagNumber": "10.1",
                     *                 "title": "Wahrnehmbar",
                     *                 "description": "",
                     *                 "level": "AAA",
                     *                 "validationResult": null,
                     *                 "childCriteria": [
                     *                   {
                     *                     "criterionId": "f7af7a1e-b1d7-4410-837f-2016700d0a91",
                     *                     "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                     "sectionType": "NON_WEB_DOC",
                     *                     "leaf": false,
                     *                     "wcagNumber": "10.1.1",
                     *                     "title": "Textalternativen",
                     *                     "description": "",
                     *                     "level": "AAA",
                     *                     "validationResult": null,
                     *                     "childCriteria": [
                     *                       {
                     *                         "criterionId": "57d9d83f-73ea-4e28-9d36-6ddf0345a38b",
                     *                         "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                         "sectionType": "NON_WEB_DOC",
                     *                         "leaf": true,
                     *                         "wcagNumber": "10.1.1.1",
                     *                         "title": "Nicht-Text-Inhalt",
                     *                         "description": "",
                     *                         "level": "AAA",
                     *                         "validationResult": null,
                     *                         "childCriteria": [],
                     *                         "checkResult": "ESSENTIALLY_PASSED",
                     *                         "perceivable": false,
                     *                         "operable": false,
                     *                         "understandable": false,
                     *                         "robust": false
                     *                       }
                     *                     ],
                     *                     "checkResult": "EMPTY",
                     *                     "perceivable": false,
                     *                     "operable": false,
                     *                     "understandable": false,
                     *                     "robust": false
                     *                   },
                     *                   {
                     *                     "criterionId": "9239c5d7-1c98-4feb-81fd-b828f1ea25ae",
                     *                     "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                     "sectionType": "NON_WEB_DOC",
                     *                     "leaf": false,
                     *                     "wcagNumber": "10.1.2",
                     *                     "title": "Zeitbasierte Medien",
                     *                     "description": "",
                     *                     "level": "AAA",
                     *                     "validationResult": null,
                     *                     "childCriteria": [
                     *                       {
                     *                         "criterionId": "fadbd103-f984-4f5d-83e2-979c4cbeef0b",
                     *                         "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                         "sectionType": "NON_WEB_DOC",
                     *                         "leaf": true,
                     *                         "wcagNumber": "10.1.2.1",
                     *                         "title": "Reines Audio und reines Video (aufgezeichnet)",
                     *                         "description": "",
                     *                         "level": "AAA",
                     *                         "validationResult": null,
                     *                         "childCriteria": [],
                     *                         "checkResult": "PASSED",
                     *                         "perceivable": false,
                     *                         "operable": false,
                     *                         "understandable": false,
                     *                         "robust": false
                     *                       },
                     *                       {
                     *                         "criterionId": "ef8fc66b-59ae-49a9-8954-d370c1a0da42",
                     *                         "nonWebDocId": "2fcd3922-82e6-4a1e-96b1-4b5b5d40caa4",
                     *                         "sectionType": "NON_WEB_DOC",
                     *                         "leaf": true,
                     *                         "wcagNumber": "10.1.2.2",
                     *                         "title": "Untertitel (aufgezeichnet)",
                     *                         "description": "",
                     *                         "level": "AAA",
                     *                         "validationResult": null,
                     *                         "childCriteria": [],
                     *                         "checkResult": "EMPTY",
                     *                         "perceivable": false,
                     *                         "operable": false,
                     *                         "understandable": false,
                     *                         "robust": false
                     *                       }
                     *                     ],
                     *                     "checkResult": "EMPTY",
                     *                     "perceivable": false,
                     *                     "operable": false,
                     *                     "understandable": false,
                     *                     "robust": false
                     *                   }
                     *                 ],
                     *                 "checkResult": "EMPTY",
                     *                 "perceivable": false,
                     *                 "operable": false,
                     *                 "understandable": false,
                     *                 "robust": false
                     *               }
                     *             ],
                     *             "checkResult": "EMPTY",
                     *             "perceivable": false,
                     *             "operable": false,
                     *             "understandable": false,
                     *             "robust": false
                     *           }
                     *         ]
                     *       }
                     *     }
                     */
                    "application/json": components["schemas"]["NonWebDocDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    submitReport: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /** @example 123-456-789 */
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    importReportFile: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "multipart/form-data": {
                    /**
                     * Format: binary
                     * @description The file to import in either EXCEL or JSON format.
                     *     Please note that the appropriate Content-Type must be set.
                     *     * EXCEL: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
                     *     * JSON: `application/json`
                     */
                    importFile: string;
                };
            };
        };
        responses: {
            /** @description CREATED */
            201: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "id": "123-456-789",
                     *       "bfitId": "1234"
                     *     }
                     */
                    "application/json": components["schemas"]["ReportIdWrapperDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    validateReportFile: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "multipart/form-data": {
                    /**
                     * Format: binary
                     * @description The file to validate in either EXCEL or JSON format.
                     *     Please note that the appropriate Content-Type must be set.
                     *     * EXCEL: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
                     *     * JSON: `application/json`
                     */
                    importFile: string;
                };
            };
        };
        responses: {
            /** @description Validation finished. Check the `valid` flag and nested field results. */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ReportImportValidationResultDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    importReportFromJson: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                /**
                 * @example {
                 *       "value": {
                 *         "recheckId": null,
                 *         "surveyType": "DETAILED",
                 *         "applicationType": "WEBSITE",
                 *         "dateOfReport": "2023-09-20",
                 *         "inspectionAuthority": "BE",
                 *         "artifactName": "Test Website",
                 *         "url": "https://test.de",
                 *         "systemDetails": [
                 *           {
                 *             "systemDetailType": "OS_WINDOWS_7"
                 *           },
                 *           {
                 *             "version": "Version 113.0.5672.126"
                 *           }
                 *         ],
                 *         "editor": "Max Mustermann",
                 *         "durationHours": 3.5,
                 *         "administrationLayer": "NATIONAL",
                 *         "publicServices": [
                 *           "SOCIAL_PROTECTION"
                 *         ],
                 *         "criteria": {
                 *           "criteriaExample": {
                 *             "$ref": "#/components/examples/criteriaExample"
                 *           }
                 *         },
                 *         "documents": {
                 *           "documentExample": {
                 *             "$ref": "#/components/examples/documentExample"
                 *           }
                 *         }
                 *       }
                 *     }
                 */
                "application/json": components["schemas"]["ReportImportDTO"];
            };
        };
        responses: {
            /** @description Report imported */
            201: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "id": "123-456-789",
                     *       "bfitId": "1234"
                     *     }
                     */
                    "application/json": components["schemas"]["ReportIdWrapperDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    readReport: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ReportDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateCriteria: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody: {
            content: {
                "application/json": components["schemas"]["CriterionResultDTO"][];
            };
        };
        responses: {
            /** @description update successful */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateDocuments: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /** @example 123-456-789 */
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody: {
            content: {
                /**
                 * @example {
                 *       "documentExample": {
                 *         "$ref": "../components/examples.yaml#/components/examples/documentExample"
                 *       }
                 *     }
                 */
                "application/json": components["schemas"]["DocumentDTO"][];
            };
        };
        responses: {
            /** @description OK */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateAdditionalCriteria: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody: {
            content: {
                "application/json": components["schemas"]["AdditionalCriterionResultDTO"][];
            };
        };
        responses: {
            /** @description update successful */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    exportMultiReports: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["IdTypeDTO"][];
            };
        };
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/zip": string;
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    exportSingleReport: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": string;
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    bulkDeleteReports: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody: {
            content: {
                "application/json": string[];
            };
        };
        responses: {
            /** @description OK */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    changeStatusMulti: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody: {
            content: {
                "application/json": components["schemas"]["ReportStatusChangeDTO"];
            };
        };
        responses: {
            /** @description OK */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getCatalogs: {
        parameters: {
            query: {
                /** @description Report date */
                dateOfReport: string;
            };
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["CatalogBasicsDTO"][];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    importCatalogFromExcel: {
        parameters: {
            query: {
                /** @description Catalog name */
                catalogName: string;
                /** @description Application type */
                applicationType: components["schemas"]["ApplicationType"];
                /** @description Valid from */
                validFrom: string;
                /** @description Valid until */
                validUntil: string;
            };
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "multipart/form-data": {
                    /**
                     * Format: binary
                     * @description The file to import
                     */
                    importFile: string;
                };
            };
        };
        responses: {
            /** @description Catalog created */
            201: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "id": "123-456-789"
                     *     }
                     */
                    "application/json": components["schemas"]["IdWrapperDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getCatalog: {
        parameters: {
            query: {
                /** @description Survey type for which the minimum recommendations are provided */
                surveyType: components["schemas"]["SurveyType"];
            };
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /**
                 * @description Database id of the catalog
                 * @example 12-456-789
                 */
                catalogId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "value": {
                     *         "catalogName": "Prüfkatalog Website 2024",
                     *         "applicationType": "WEBSITE",
                     *         "validFrom": "2024-01-01",
                     *         "validUntil": "2024-12-31",
                     *         "reportCriteria": {
                     *           "$ref": "#/components/examples/criteriaExample"
                     *         },
                     *         "documentCriteria": {
                     *           "$ref": "#/components/examples/criteriaExample"
                     *         }
                     *       }
                     *     }
                     */
                    "application/json": components["schemas"]["CatalogDTO"];
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    deleteCatalog: {
        parameters: {
            query?: never;
            header?: never;
            path: {
                /**
                 * @description Id of the catalog
                 * @example 12-456-789
                 */
                catalogId: string;
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description Successfully deleted */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data or the there are still report referencing the catalog. */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getImportTemplate: {
        parameters: {
            query?: {
                /** @description format of the template: xlsx or json */
                format?: components["schemas"]["FormatType"];
            };
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /**
                 * @description Database id of the catalog
                 * @example 12-456-789
                 */
                catalogId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/octet-stream": string;
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getUserInfo: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "id": "213ef7c5-f84b-42e9-842c-fdd873a86bc0",
                     *       "givenName": "Max",
                     *       "surname": "Mustermann",
                     *       "permissionLevel": "USER",
                     *       "inspectionAuthorities": [
                     *         "BW"
                     *       ]
                     *     }
                     */
                    "application/json": components["schemas"]["UserDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getApiKey: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "text/plain": string;
                };
            };
            /** @description API key not created yet. */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    checkApiKeyExists: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "value": false
                     *     }
                     */
                    "application/json": components["schemas"]["BooleanWrapperDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    generateKeyFigures: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path?: never;
            cookie?: never;
        };
        requestBody: {
            content: {
                "application/json": components["schemas"]["KeyFiguresRequestDTO"];
            };
        };
        responses: {
            /** @description The response is an excel file with all selected key figures */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/octet-stream": string;
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getAvailableKeyFigures: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["KeyFigureCategoryDTO"][];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    searchReports: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["SearchReportsRequestDTO"];
            };
        };
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "total": 2,
                     *       "page": 0,
                     *       "size": 20,
                     *       "results": [
                     *         {
                     *           "bfitId": "8000",
                     *           "inspectionAuthority": "BE",
                     *           "applicationType": "WEBSITE",
                     *           "surveyType": "DETAILED",
                     *           "artifactName": "Test Website",
                     *           "dateOfReport": "2023-09-20",
                     *           "status": "IN_PROGRESS",
                     *           "minRecommendationValid": true,
                     *           "editor": "Max Mustermann",
                     *           "administrationLayer": "LOCAL"
                     *         },
                     *         {
                     *           "bfitId": "8019",
                     *           "inspectionAuthority": "BE",
                     *           "applicationType": "WEBSITE",
                     *           "surveyType": "DETAILED",
                     *           "artifactName": "Test Website_19",
                     *           "dateOfReport": "2023-10-09",
                     *           "status": "COMPLETED",
                     *           "minRecommendationValid": true,
                     *           "editor": "Max Mustermann",
                     *           "administrationLayer": "OTHERS"
                     *         }
                     *       ]
                     *     }
                     */
                    "application/json": components["schemas"]["SearchReportsResponseDTO"];
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    searchReportsAsExcel: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["SearchReportsRequestDTO"];
            };
        };
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": string;
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    searchCatalogs: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["SearchCatalogsRequestDTO"];
            };
        };
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "total": 2,
                     *       "page": 0,
                     *       "size": 20,
                     *       "results": [
                     *         {
                     *           "id": "8000",
                     *           "applicationType": "WEBSITE",
                     *           "name": "Test Catalog",
                     *           "validFrom": "2023-09-20",
                     *           "validUntil": "2024-09-20",
                     *           "reportCount": 12
                     *         },
                     *         {
                     *           "id": "8001",
                     *           "applicationType": "MOBILE_APPLICATION",
                     *           "name": "Another Catalog",
                     *           "validFrom": "2020-09-20",
                     *           "validUntil": "2025-09-20",
                     *           "reportCount": 0
                     *         }
                     *       ]
                     *     }
                     */
                    "application/json": components["schemas"]["SearchCatalogsResponseDTO"];
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getApplicationLogs: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description The response is a log file containing the logfile. */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/octet-stream": string;
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getApiKeyUsers: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description The response contains all existing users having an API keys. */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example [
                     *       {
                     *         "userId": "1234",
                     *         "description": "Überwachungsstelle Bayern"
                     *       },
                     *       {
                     *         "userId": "4321"
                     *       }
                     *     ]
                     */
                    "application/json": components["schemas"]["ApiKeyUserDTO"][];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getApiKeyForUser: {
        parameters: {
            query?: never;
            header?: never;
            path: {
                /**
                 * @description Azure user id
                 * @example 12-456-789
                 */
                userId: string;
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description The response contains the API key. */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "text/plain": string;
                };
            };
            /** @description API key not created yet. */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    createApiKey: {
        parameters: {
            query?: {
                /** @description Description field for the api key */
                description?: string;
            };
            header?: never;
            path: {
                /**
                 * @description Azure user id
                 * @example 12-456-789
                 */
                userId: string;
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description API key successfully created */
            201: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "text/plain": string;
                };
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    deleteApiKey: {
        parameters: {
            query?: never;
            header?: never;
            path: {
                /**
                 * @description Azure user id
                 * @example 12-456-789
                 */
                userId: string;
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description API key successfully deleted */
            204: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
            /** @description Bad request. Request parameters are empty or contain invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    updateReportVisibilities: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody: {
            content: {
                /**
                 * @example [
                 *       {
                 *         "id": "550e8400-e29b-11d4-a716-446655440000",
                 *         "publicAccessible": true
                 *       },
                 *       {
                 *         "id": "660e8400-e29b-11d4-a716-446655440001",
                 *         "publicAccessible": false
                 *       }
                 *     ]
                 */
                "application/json": components["schemas"]["ReportVisibilityUpdateDTO"][];
            };
        };
        responses: {
            /** @description Returns a list of successfully updated report visibilities */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example [
                     *       {
                     *         "id": "550e8400-e29b-11d4-a716-446655440000",
                     *         "publicAccessible": true
                     *       },
                     *       {
                     *         "id": "660e8400-e29b-11d4-a716-446655440001",
                     *         "publicAccessible": false
                     *       }
                     *     ]
                     */
                    "application/json": components["schemas"]["ReportVisibilityUpdateDTO"][];
                };
            };
            /** @description Bad request. Request body is null */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            401: components["responses"]["NotAuthorized"];
            403: components["responses"]["InsufficientPermission"];
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getPublicReport: {
        parameters: {
            query?: never;
            header?: {
                /** @description The current language of the client */
                "BFITRTool-Language"?: components["parameters"]["LanguageHttpHeader"];
            };
            path: {
                /** @description The ID of the report to retrieve */
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "id": "123-456-789",
                     *       "bfitId": "1234",
                     *       "dateOfReport": "2023-09-20",
                     *       "surveyType": "DETAILED",
                     *       "inspectionAuthority": "BE",
                     *       "artifactName": "Test Website",
                     *       "url": "https://test.de",
                     *       "recheckId": null,
                     *       "systemDetails": [
                     *         {
                     *           "systemDetailType": "CMS",
                     *           "name": "WordPress",
                     *           "version": "6.0"
                     *         }
                     *       ],
                     *       "administrationLayer": "NATIONAL",
                     *       "publicServices": [
                     *         "SOCIAL_PROTECTION"
                     *       ],
                     *       "applicationType": "WEBSITE",
                     *       "criteria": [
                     *         {
                     *           "criterionId": "criterion-1",
                     *           "checkResult": "OK",
                     *           "comment": "All requirements met"
                     *         }
                     *       ],
                     *       "additionalCriteria": [
                     *         {
                     *           "additionalCriterionId": "additional-1",
                     *           "checkResult": "WARNING",
                     *           "comment": "Minor issues found"
                     *         }
                     *       ],
                     *       "documents": [
                     *         {
                     *           "id": "doc-1",
                     *           "name": "Test Document",
                     *           "url": "https://example.com/doc.pdf"
                     *         }
                     *       ]
                     *     }
                     */
                    "application/json": components["schemas"]["PublicReportDTO"];
                };
            };
            /** @description Bad request. Request parameters are invalid */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            /** @description Report not found or not publicly accessible */
            404: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    searchPublicReports: {
        parameters: {
            query?: never;
            header?: never;
            path?: never;
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["PublicSearchReportsRequestDTO"];
            };
        };
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    /**
                     * @example {
                     *       "total": 2,
                     *       "page": 0,
                     *       "size": 20,
                     *       "results": [
                     *         {
                     *           "id": "123-456-789",
                     *           "bfitId": "8000",
                     *           "inspectionAuthority": "BE",
                     *           "applicationType": "WEBSITE",
                     *           "surveyType": "DETAILED",
                     *           "dateOfReport": "2023-09-20",
                     *           "administrationLayer": "LOCAL"
                     *         },
                     *         {
                     *           "id": "987-654-321",
                     *           "bfitId": "8019",
                     *           "inspectionAuthority": "BE",
                     *           "applicationType": "WEBSITE",
                     *           "surveyType": "DETAILED",
                     *           "dateOfReport": "2023-10-09",
                     *           "administrationLayer": "NATIONAL"
                     *         }
                     *       ]
                     *     }
                     */
                    "application/json": components["schemas"]["PublicSearchReportsResponseDTO"];
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    exportPublicReports: {
        parameters: {
            query?: never;
            header?: never;
            path: {
                /** @description The export format (JSON or XLSX) */
                type: components["schemas"]["FormatType"];
            };
            cookie?: never;
        };
        requestBody?: {
            content: {
                "application/json": components["schemas"]["PublicSearchReportsRequestDTO"];
            };
        };
        responses: {
            /** @description OK */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": string;
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": string;
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
    getPublicPdfReport: {
        parameters: {
            query?: never;
            header?: never;
            path: {
                /** @description The ID of the report to retrieve */
                reportId: components["schemas"]["IdTypeDTO"];
            };
            cookie?: never;
        };
        requestBody?: never;
        responses: {
            /** @description The response is the pdf file with the original report as a blob */
            200: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/pdf": string;
                };
            };
            /** @description Bad request. Request Body is empty or contains invalid data */
            400: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            /** @description PDF of report not found or report not publicly accessible */
            404: {
                headers: {
                    [name: string]: unknown;
                };
                content: {
                    "application/json": components["schemas"]["ErrorDTO"];
                };
            };
            /** @description Internal Server Error */
            500: {
                headers: {
                    [name: string]: unknown;
                };
                content?: never;
            };
        };
    };
}

export enum BooleanResult {
	EMPTY = "EMPTY",
	NOT_CHECKED = "NOT_CHECKED",
	YES = "YES",
	NO = "NO"
}

export enum StringMessageErrorResponseDTO {
	STRING_MESSAGE = "STRING_MESSAGE"
}

export enum ValidationFailedErrorResponseDTO {
	VALIDATION_ERROR = "VALIDATION_ERROR"
}

export enum Language {
	de = "de",
	en = "en"
}

export enum ApplicationType {
	MOBILE_APPLICATION = "MOBILE_APPLICATION",
	WEBSITE = "WEBSITE"
}

export enum SurveyType {
	SIMPLIFIED = "SIMPLIFIED",
	DETAILED = "DETAILED"
}

export enum InspectionAuthority {
	BW = "BW",
	BY = "BY",
	BE = "BE",
	BB = "BB",
	HB = "HB",
	HH = "HH",
	HE = "HE",
	MV = "MV",
	NI = "NI",
	NW = "NW",
	RP = "RP",
	SL = "SL",
	SN = "SN",
	ST = "ST",
	SH = "SH",
	TH = "TH",
	BUND = "BUND"
}

export enum ErrorCode {
	ARTIFACT_NAME_ALREADY_USED = "ARTIFACT_NAME_ALREADY_USED",
	ILLEGAL_ARGUMENT = "ILLEGAL_ARGUMENT",
	INTERNAL_ERROR = "INTERNAL_ERROR",
	INVALID_CATALOG_ID = "INVALID_CATALOG_ID",
	INVALID_CONTENT_TYPE = "INVALID_CONTENT_TYPE",
	INVALID_IMPORT_FILE = "INVALID_IMPORT_FILE",
	MANDATORY_FIELD_VIOLATION = "MANDATORY_FIELD_VIOLATION",
	OBJECT_NOT_FOUND = "OBJECT_NOT_FOUND",
	UNAUTHORIZED = "UNAUTHORIZED",
	FORBIDDEN = "FORBIDDEN",
	FUTURE_DATE = "FUTURE_DATE",
	INCOMPATIBLE_APPLICATION_AND_SURVEY_TYPES = "INCOMPATIBLE_APPLICATION_AND_SURVEY_TYPES",
	WRONG_RECHECK_DATE = "WRONG_RECHECK_DATE",
	WRONG_DURATION_HOURS = "WRONG_DURATION_HOURS",
	NUMBER_FORMAT = "NUMBER_FORMAT",
	INVALID_RECHECK_ID = "INVALID_RECHECK_ID",
	NO_INSPECTION_AUTHORITY_PROVIDED = "NO_INSPECTION_AUTHORITY_PROVIDED",
	API_KEY_USER_EXISTS = "API_KEY_USER_EXISTS",
	USER_ID_NOT_FOUND = "USER_ID_NOT_FOUND",
	CRITERIA_NOT_FOUND = "CRITERIA_NOT_FOUND",
	ADDITIONAL_CRITERIA_NOT_FOUND = "ADDITIONAL_CRITERIA_NOT_FOUND",
	CRITERIA_VALUE_INVALID = "CRITERIA_VALUE_INVALID"
}

export enum SystemDetailType {
	OS_WINDOWS_7 = "OS_WINDOWS_7",
	OS_WINDOWS_10 = "OS_WINDOWS_10",
	OS_WINDOWS_11 = "OS_WINDOWS_11",
	OS_LINUX = "OS_LINUX",
	OS_MAC_OS = "OS_MAC_OS",
	OS_IOS = "OS_IOS",
	OS_ANDROID = "OS_ANDROID",
	BROWSER_MICROSOFT_EDGE = "BROWSER_MICROSOFT_EDGE",
	BROWSER_FIREFOX = "BROWSER_FIREFOX",
	BROWSER_CHROME = "BROWSER_CHROME",
	BROWSER_SELENIUM = "BROWSER_SELENIUM",
	BROWSER_SAFARI = "BROWSER_SAFARI",
	ASSIST_NVDA = "ASSIST_NVDA",
	ASSIST_JAWS = "ASSIST_JAWS",
	ASSIST_ZOOMTEXT = "ASSIST_ZOOMTEXT",
	ASSIST_VOICEOVER = "ASSIST_VOICEOVER",
	ASSIST_TALKBACK = "ASSIST_TALKBACK"
}

export enum AdministrationLayer {
	NATIONAL = "NATIONAL",
	REGIONAL = "REGIONAL",
	LOCAL = "LOCAL",
	OTHERS = "OTHERS"
}

export enum PublicServiceType {
	SOCIAL_PROTECTION = "SOCIAL_PROTECTION",
	HEALTHCARE = "HEALTHCARE",
	TRAFFIC = "TRAFFIC",
	EDUCATION = "EDUCATION",
	EMPLOYMENT_AND_TAXES = "EMPLOYMENT_AND_TAXES",
	ENVIRONMENTAL_PROTECTION = "ENVIRONMENTAL_PROTECTION",
	LEISURE_AND_CULTURE = "LEISURE_AND_CULTURE",
	HOUSING_AND_COMMUNAL_FACILITIES = "HOUSING_AND_COMMUNAL_FACILITIES",
	PUBLIC_ORDER_AND_SECURITY = "PUBLIC_ORDER_AND_SECURITY",
	MISCELLANEOUS = "MISCELLANEOUS"
}

export enum SectionType {
	REPORT = "REPORT",
	NON_WEB_DOC = "NON_WEB_DOC"
}

export enum ValidationResult {
	OK = "OK",
	WARNING = "WARNING"
}

export enum CheckResult {
	EMPTY = "EMPTY",
	NOT_CHECKED = "NOT_CHECKED",
	PASSED = "PASSED",
	ESSENTIALLY_PASSED = "ESSENTIALLY_PASSED",
	FAILED = "FAILED",
	NOT_APPLICABLE = "NOT_APPLICABLE"
}

export enum DocumentType {
	PDF = "PDF",
	XLSX = "XLSX",
	DOCX = "DOCX",
	PPTX = "PPTX"
}

export enum ListValidationStatus {
	VALID = "VALID",
	INVALID_LIST_EMPTY = "INVALID_LIST_EMPTY",
	INVALID_ITEM = "INVALID_ITEM"
}

export enum ReportStatusType {
	IN_PROGRESS = "IN_PROGRESS",
	DISPATCHED = "DISPATCHED",
	COMPLETED = "COMPLETED"
}

export enum AdditionalCriterionResultType {
	CHECK_RESULT = "CHECK_RESULT",
	BOOLEAN_RESULT = "BOOLEAN_RESULT"
}

export enum FormatType {
	XLSX = "XLSX",
	JSON = "JSON"
}

export enum PermissionLevel {
	USER = "USER",
	ADMIN = "ADMIN",
	SERVICE_PROVIDER = "SERVICE_PROVIDER"
}

export enum KeyFigureType {
	REPORT_OVERVIEW = "REPORT_OVERVIEW",
	REPORTS_PER_ADMINISTRATION_LAYER = "REPORTS_PER_ADMINISTRATION_LAYER",
	REPORTS_PER_PUBLIC_SERVICE = "REPORTS_PER_PUBLIC_SERVICE",
	REPORTS_PER_OS = "REPORTS_PER_OS",
	BREAKDOWN_OF_SYSTEM_DETAILS = "BREAKDOWN_OF_SYSTEM_DETAILS",
	WEBSITE_SIMPLIFIED_REPORTS_WITHOUT_ADDITIONAL_CRITERIA = "WEBSITE_SIMPLIFIED_REPORTS_WITHOUT_ADDITIONAL_CRITERIA",
	WEBSITE_SIMPLIFIED_REPORTS_ADDITIONAL_CRITERIA_ONLY = "WEBSITE_SIMPLIFIED_REPORTS_ADDITIONAL_CRITERIA_ONLY",
	WEBSITE_DETAILED_REPORTS_WITHOUT_ADDITIONAL_CRITERIA = "WEBSITE_DETAILED_REPORTS_WITHOUT_ADDITIONAL_CRITERIA",
	WEBSITE_DETAILED_REPORTS_ADDITIONAL_CRITERIA_ONLY = "WEBSITE_DETAILED_REPORTS_ADDITIONAL_CRITERIA_ONLY",
	MOBILE_APP_REPORTS_WITHOUT_ADDITIONAL_CRITERIA = "MOBILE_APP_REPORTS_WITHOUT_ADDITIONAL_CRITERIA",
	MOBILE_APP_REPORTS_ADDITIONAL_CRITERIA_ONLY = "MOBILE_APP_REPORTS_ADDITIONAL_CRITERIA_ONLY",
	DETAILED_REPORT_DOCUMENTS_CRITERIA = "DETAILED_REPORT_DOCUMENTS_CRITERIA",
	CRITERIA_BREAKDOWN = "CRITERIA_BREAKDOWN",
	WEBSITE_PRINCIPLES_RESULT_COMPARISON = "WEBSITE_PRINCIPLES_RESULT_COMPARISON",
	DOCUMENT_PRINCIPLES_RESULT_COMPARISON = "DOCUMENT_PRINCIPLES_RESULT_COMPARISON",
	MOBILE_APP_PRINCIPLES_RESULT_COMPARISON = "MOBILE_APP_PRINCIPLES_RESULT_COMPARISON",
	ALL_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON = "ALL_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON",
	INITIAL_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON = "INITIAL_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON",
	RECHECK_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON = "RECHECK_WEBSITE_SIMPLIFIED_CRITERIA_COMPARISON",
	ALL_WEBSITE_DETAILED_CRITERIA_COMPARISON = "ALL_WEBSITE_DETAILED_CRITERIA_COMPARISON",
	INITIAL_WEBSITE_DETAILED_CRITERIA_COMPARISON = "INITIAL_WEBSITE_DETAILED_CRITERIA_COMPARISON",
	RECHECK_WEBSITE_DETAILED_CRITERIA_COMPARISON = "RECHECK_WEBSITE_DETAILED_CRITERIA_COMPARISON",
	ALL_MOBILE_APP_CRITERIA_COMPARISON = "ALL_MOBILE_APP_CRITERIA_COMPARISON",
	INITIAL_MOBILE_APP_CRITERIA_COMPARISON = "INITIAL_MOBILE_APP_CRITERIA_COMPARISON",
	RECHECK_MOBILE_APP_CRITERIA_COMPARISON = "RECHECK_MOBILE_APP_CRITERIA_COMPARISON",
	ADMINISTRATION_LAYERS_COMPARISON = "ADMINISTRATION_LAYERS_COMPARISON",
	PUBLIC_SERVICES_COMPARISON = "PUBLIC_SERVICES_COMPARISON",
	ACCESSIBILITY_DECLARATION_RESULT_COMPARISON = "ACCESSIBILITY_DECLARATION_RESULT_COMPARISON",
	PAC_PDF_CHECK_RESULTS_COMPARISON = "PAC_PDF_CHECK_RESULTS_COMPARISON",
	MANUAL_PDF_CHECK_RESULTS_COMPARISON = "MANUAL_PDF_CHECK_RESULTS_COMPARISON",
	ADDITIONAL_CRITERIA_COMPARISON = "ADDITIONAL_CRITERIA_COMPARISON",
	RECHECK_REPORTS_COMPARISON = "RECHECK_REPORTS_COMPARISON",
	SAMPLE_ADMINISTRATION_LAYERS_YEAR_COMPARISON = "SAMPLE_ADMINISTRATION_LAYERS_YEAR_COMPARISON",
	SAMPLE_PUBLIC_SERVICES_YEAR_COMPARISON = "SAMPLE_PUBLIC_SERVICES_YEAR_COMPARISON",
	SAMPLE_MOBILE_OS_YEAR_COMPARISON = "SAMPLE_MOBILE_OS_YEAR_COMPARISON",
	SAMPLE_RECHECK_YEAR_COMPARISON = "SAMPLE_RECHECK_YEAR_COMPARISON",
	SAMPLE_CHECKS_BY_OS = "SAMPLE_CHECKS_BY_OS",
	SAMPLE_CHECKS_BY_BROWSER = "SAMPLE_CHECKS_BY_BROWSER",
	SAMPLE_CHECKS_BY_SCREENREADER = "SAMPLE_CHECKS_BY_SCREENREADER"
}

export enum KeyFigureCategoryType {
	GLOBAL = "GLOBAL",
	CRITERIA_BASED = "CRITERIA_BASED",
	YEAR_BASED = "YEAR_BASED",
	YEAR_AND_CRITERIA_BASED = "YEAR_AND_CRITERIA_BASED",
	SAMPLE_DISTRIBUTION = "SAMPLE_DISTRIBUTION"
}

export enum SearchField {
	bfitId = "bfitId",
	inspectionAuthority = "inspectionAuthority",
	applicationType = "applicationType",
	surveyType = "surveyType",
	artifactName = "artifactName",
	dateOfReport = "dateOfReport",
	status = "status",
	minRecommendationValid = "minRecommendationValid",
	editor = "editor",
	administrationLayer = "administrationLayer",
	publicAccessible = "publicAccessible"
}

export enum SortDTO {
	ASC = "ASC",
	DESC = "DESC"
}

export enum SearchCatalogField {
	id = "id",
	name = "name",
	applicationType = "applicationType",
	validFrom = "validFrom",
	validUntil = "validUntil",
	reportCount = "reportCount"
}

export enum SortCatalogDTO {
	ASC = "ASC",
	DESC = "DESC"
}